import { App } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { Preferences } from "@capacitor/preferences";
import { spErrorTags } from "@eventr/common";

/** Preferences key. Outlives the webview reload that a bundle swap performs. */
const SPENT_URL_KEY = "spentLaunchUrl";

// The URL this webview has opened. Module state, so a reload forgets it, which is what lets the
// recovery below re-open a link the reload interrupted.
let openedUrl: string | undefined;

let recovered = false;

/** Notes `raw` as opened here, so the recovery below does not open it a second time. */
export function recordOpenedUrl(raw: string): void {
  openedUrl = raw;
}

/**
 * Marks the URL being opened as spent: its effect has happened and cannot happen twice, as with
 * a redeemed invite token. A reload after this point re-opens nothing.
 */
export function markOpenedUrlSpent(): void {
  if (!openedUrl) return;
  void Preferences.set({ key: SPENT_URL_KEY, value: openedUrl });
}

/**
 * Opens the URL the app was launched at, when this webview has not already opened it.
 *
 * `appUrlOpen` is a one-shot, and a @capgo bundle swap reloads the webview under it — on the
 * first launch after an install, in the seconds a link takes to resolve. Nothing replays the
 * event into the new webview, but `App.getLaunchUrl()` reads the same URL back off the native
 * side, which the reload leaves alone.
 *
 * One-shot per webview, and a no-op for a URL already opened here or already spent.
 */
export async function recoverLaunchUrl(open: (url: string) => void): Promise<void> {
  if (recovered) return;
  recovered = true;
  if (!Capacitor.isNativePlatform()) return;

  try {
    const url = (await App.getLaunchUrl())?.url;
    if (!url || url === openedUrl) return;
    if ((await Preferences.get({ key: SPENT_URL_KEY })).value === url) return;
    recordOpenedUrl(url);
    open(url);
  } catch (error) {
    spErrorTags("Failed to recover the launch URL", ["DEEPLINK"], error);
  }
}
